import { useEffect, useState } from "react";
import axios from "axios";
import { useTheme } from "@/theme/ThemeContext";
import { ensureContrast } from "@/lib/contrast";

// Card backgrounds the accent text sits on, per theme (see index.css)
const CARD_BG = { dark: "#111827", light: "#F4F7F5" };

export function useBrandTheme(branding) {
  const { theme } = useTheme();
  useEffect(() => {
    const root = document.documentElement;
    if (branding?.primary) root.style.setProperty("--hp-primary", branding.primary);
    // The accent is used as text colour, so keep it readable on this theme's card background
    if (branding?.accent) root.style.setProperty("--hp-accent", ensureContrast(branding.accent, CARD_BG[theme] || CARD_BG.dark));
    return () => { root.style.removeProperty("--hp-primary"); root.style.removeProperty("--hp-accent"); };
  }, [branding?.primary, branding?.accent, theme]);
}

export function usePublicBranding(slug) {
  const [b, setB] = useState(null);
  useEffect(() => { if (!slug) return; axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/branding/${slug}`).then((r) => setB(r.data)).catch(() => setB(null)); }, [slug]);
  return b;
}

export const brandSlugFromPath = (pathname) => { const m = pathname.match(/^\/(?:family|embed|kiosk|activities)\/([a-z0-9-]+)/i); return m ? m[1] : ""; };

// Fixed platform brand — the booking system itself. Individual programmes cannot change this;
// they add their own logo/name which appears as a SUB-brand alongside it.
export const PLATFORM = { name: "Out of School Hub", tagline: "Connecting care & fun", logo: "/oosh-logo.png" };

export function BrandMark({ branding, size = "md", subtitle, light = false }) {
  const box = size === "lg" ? "h-14 w-14 rounded-2xl" : size === "sm" ? "h-10 w-10 rounded-xl" : "h-12 w-12 rounded-2xl";
  const progName = branding?.name && branding.name !== PLATFORM.name ? branding.name : "";
  const progLogo = branding?.logoUrl ? `${process.env.REACT_APP_BACKEND_URL}${branding.logoUrl}` : "";
  const showSub = !!(progName || progLogo);
  return (
    <div className="flex items-center gap-3" data-testid="brand-mark">
      <img src={PLATFORM.logo} alt={`${PLATFORM.name} logo`} className={`${box} object-contain bg-white p-0.5 shadow-lg shadow-black/30`} data-testid="brand-logo" />
      <div>
        <p className={`font-head ${size === "lg" ? "text-2xl" : size === "sm" ? "text-base" : "text-lg"} font-extrabold leading-none tracking-tight ${light ? "text-[#04231a]" : "text-[var(--hp-heading)]"}`} data-testid="brand-name">{PLATFORM.name}</p>
        <p className="text-xs uppercase tracking-[0.18em] text-[var(--hp-muted)] mt-1.5">{subtitle || PLATFORM.tagline}</p>
        {showSub && (
          <div className="mt-1.5 flex items-center gap-1.5" data-testid="brand-subbrand">
            {progLogo && <img src={progLogo} alt={progName || "Programme"} className="h-4 w-4 rounded object-contain bg-white/95" data-testid="brand-subbrand-logo" />}
            <span className="text-xs font-semibold text-[var(--hp-muted)]" data-testid="brand-subbrand-name">{progName || "Programme"}</span>
          </div>
        )}
      </div>
    </div>
  );
}
